import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  LinearProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  LocalHospital as HospitalIcon,
  EventBusy as NoShowIcon,
  Hotel as BedIcon,
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Sms as SmsIcon,
  CheckCircle as CheckIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import {
  getReadmissionRiskSummary,
  getHighRiskReadmissionPatients,
  getNoShowPredictions,
  getNoShowDailySummary,
  getPopulationHealthSummary,
  getHighRiskPatients,
  getPatientRiskProfile,
  getModelPerformance,
  getRiskTierColor,
  getRiskTierLabel,
  formatProbabilityPercent,
  getNoShowActionText,
  getInterventionDetails
} from '../../services/predictiveAnalyticsService';
import { useUserData } from '../../hooks/useUserData';

// Styled Components
const HeaderCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  marginBottom: theme.spacing(3),
  borderRadius: '12px',
  boxShadow: '0 4px 20px rgba(102, 126, 234, 0.2)',
}));

const ModernCard = styled(Card)(({ theme }) => ({
  borderRadius: '16px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  height: '100%',
}));

const StatCard = styled(Card)(({ theme, color }) => ({
  borderRadius: '12px',
  boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
  background: `linear-gradient(135deg, ${color || '#667eea'} 0%, ${color ? adjustColor(color, -20) : '#764ba2'} 100%)`,
  color: 'white',
  height: '100%',
}));

const RiskChip = styled(Chip)(({ riskcolor }) => ({
  backgroundColor: riskcolor,
  color: 'white',
  fontWeight: 600,
  fontSize: '0.75rem',
}));

// Helper function to adjust color brightness
function adjustColor(color, amount) {
  const clamp = (num) => Math.min(255, Math.max(0, num));
  const hex = color.replace('#', '');
  const r = clamp(parseInt(hex.substring(0, 2), 16) + amount);
  const g = clamp(parseInt(hex.substring(2, 4), 16) + amount);
  const b = clamp(parseInt(hex.substring(4, 6), 16) + amount);
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

// Sub-tabs for predictive analytics
const TABS = {
  OVERVIEW: 0,
  READMISSION: 1,
  NO_SHOW: 2,
  POPULATION: 3
};

export default function PredictiveAnalytics() {
  const [activeTab, setActiveTab] = useState(TABS.OVERVIEW);
  const [error, setError] = useState(null);
  const { user } = useUserData();
  
  // Data states
  const [readmissionSummary, setReadmissionSummary] = useState(null);
  const [highRiskReadmissions, setHighRiskReadmissions] = useState([]);
  const [noShowPredictions, setNoShowPredictions] = useState([]);
  const [noShowSummary, setNoShowSummary] = useState([]);
  const [populationSummary, setPopulationSummary] = useState(null);
  const [highRiskPatients, setHighRiskPatients] = useState([]);
  const [modelPerformance, setModelPerformance] = useState(null);
  
  // Per-tab loading states (for better UX)
  const [loadingTabs, setLoadingTabs] = useState({
    [TABS.OVERVIEW]: true,  // Start true since we load on mount
    [TABS.READMISSION]: false,
    [TABS.NOSHOW]: false,
    [TABS.POPULATION]: false
  });
  
  // Track which tabs have been loaded (for lazy loading)
  const [loadedTabs, setLoadedTabs] = useState({
    [TABS.OVERVIEW]: false,
    [TABS.READMISSION]: false,
    [TABS.NOSHOW]: false,
    [TABS.POPULATION]: false
  });
  
  // Dialog states
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientRiskProfile, setPatientRiskProfile] = useState(null);
  const [showPatientDialog, setShowPatientDialog] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(false);
  
  // Pagination state
  const [readmissionPage, setReadmissionPage] = useState(0);
  const [readmissionRowsPerPage, setReadmissionRowsPerPage] = useState(10);
  const [populationPage, setPopulationPage] = useState(0);
  const [populationRowsPerPage, setPopulationRowsPerPage] = useState(10);
  const [noShowPage, setNoShowPage] = useState(0);
  const [noShowRowsPerPage, setNoShowRowsPerPage] = useState(10);

  const facilityId = user?.defaultFacility?.id;
  
  // Helper for global loading state
  const loading = Object.values(loadingTabs).some(v => v);

  // Fetch overview data (minimal data for overview tab)
  const fetchOverviewData = useCallback(async () => {
    if (!facilityId || loadedTabs[TABS.OVERVIEW]) return;
    
    try {
      setLoadingTabs(prev => ({ ...prev, [TABS.OVERVIEW]: true }));
      setError(null);
      
      // Only fetch summary data needed for overview cards
      const [readmitSummary, noShowDaily, popSummary, readmitPatients] = await Promise.allSettled([
        getReadmissionRiskSummary(facilityId),
        getNoShowDailySummary(facilityId),
        getPopulationHealthSummary(facilityId),
        getHighRiskReadmissionPatients(facilityId, 5) // Only 5 for overview preview
      ]);
      
      if (readmitSummary.status === 'fulfilled') {
        setReadmissionSummary(readmitSummary.value);
      }
      if (noShowDaily.status === 'fulfilled') {
        setNoShowSummary(noShowDaily.value?.dailySummary || []);
      }
      if (popSummary.status === 'fulfilled') {
        setPopulationSummary(popSummary.value);
      }
      if (readmitPatients.status === 'fulfilled') {
        setHighRiskReadmissions(readmitPatients.value?.patients || []);
      }
      
      setLoadedTabs(prev => ({ ...prev, [TABS.OVERVIEW]: true }));
    } catch (err) {
      console.error('Error fetching overview data:', err);
      setError('Failed to load overview data. Please try again.');
    } finally {
      setLoadingTabs(prev => ({ ...prev, [TABS.OVERVIEW]: false }));
    }
  }, [facilityId, loadedTabs]);

  // Fetch readmission tab data
  const fetchReadmissionData = useCallback(async () => {
    if (!facilityId || loadedTabs[TABS.READMISSION]) return;
    
    try {
      setLoadingTabs(prev => ({ ...prev, [TABS.READMISSION]: true }));
      const [readmitPatients] = await Promise.allSettled([
        getHighRiskReadmissionPatients(facilityId, 50)
      ]);
      
      if (readmitPatients.status === 'fulfilled') {
        setHighRiskReadmissions(readmitPatients.value?.patients || []);
      }
      
      setLoadedTabs(prev => ({ ...prev, [TABS.READMISSION]: true }));
    } catch (err) {
      console.error('Error fetching readmission data:', err);
    } finally {
      setLoadingTabs(prev => ({ ...prev, [TABS.READMISSION]: false }));
    }
  }, [facilityId, loadedTabs]);

  // Fetch no-show tab data
  const fetchNoShowData = useCallback(async () => {
    if (!facilityId || loadedTabs[TABS.NOSHOW]) return;
    
    try {
      setLoadingTabs(prev => ({ ...prev, [TABS.NOSHOW]: true }));
      const [noShowList] = await Promise.allSettled([
        getNoShowPredictions(facilityId, 7)
      ]);
      
      if (noShowList.status === 'fulfilled') {
        setNoShowPredictions(noShowList.value?.appointments || []);
      }
      
      setLoadedTabs(prev => ({ ...prev, [TABS.NOSHOW]: true }));
    } catch (err) {
      console.error('Error fetching no-show data:', err);
    } finally {
      setLoadingTabs(prev => ({ ...prev, [TABS.NOSHOW]: false }));
    }
  }, [facilityId, loadedTabs]);

  // Fetch population health tab data
  const fetchPopulationData = useCallback(async () => {
    if (!facilityId || loadedTabs[TABS.POPULATION]) return;
    
    try {
      setLoadingTabs(prev => ({ ...prev, [TABS.POPULATION]: true }));
      const [riskPatients, models] = await Promise.allSettled([
        getHighRiskPatients(facilityId, 50),
        getModelPerformance()
      ]);
      
      if (riskPatients.status === 'fulfilled') {
        setHighRiskPatients(riskPatients.value?.patients || []);
      }
      if (models.status === 'fulfilled') {
        setModelPerformance(models.value);
      }
      
      setLoadedTabs(prev => ({ ...prev, [TABS.POPULATION]: true }));
    } catch (err) {
      console.error('Error fetching population data:', err);
    } finally {
      setLoadingTabs(prev => ({ ...prev, [TABS.POPULATION]: false }));
    }
  }, [facilityId, loadedTabs]);

  // Fetch all data (for refresh button)
  const fetchData = useCallback(async () => {
    // Reset loaded tabs to force refresh
    setLoadedTabs({
      [TABS.OVERVIEW]: false,
      [TABS.READMISSION]: false,
      [TABS.NOSHOW]: false,
      [TABS.POPULATION]: false
    });
    
    // Fetch current tab data
    if (activeTab === TABS.OVERVIEW) {
      await fetchOverviewData();
    } else if (activeTab === TABS.READMISSION) {
      await fetchReadmissionData();
    } else if (activeTab === TABS.NOSHOW) {
      await fetchNoShowData();
    } else if (activeTab === TABS.POPULATION) {
      await fetchPopulationData();
    }
  }, [activeTab, fetchOverviewData, fetchReadmissionData, fetchNoShowData, fetchPopulationData]);

  // Load initial overview data
  useEffect(() => {
    fetchOverviewData();
  }, [fetchOverviewData]);

  // Lazy load tab data when tab changes
  useEffect(() => {
    if (activeTab === TABS.READMISSION && !loadedTabs[TABS.READMISSION]) {
      fetchReadmissionData();
    } else if (activeTab === TABS.NOSHOW && !loadedTabs[TABS.NOSHOW]) {
      fetchNoShowData();
    } else if (activeTab === TABS.POPULATION && !loadedTabs[TABS.POPULATION]) {
      fetchPopulationData();
    }
  }, [activeTab, loadedTabs, fetchReadmissionData, fetchNoShowData, fetchPopulationData]);

  // Handle patient profile view
  const handleViewPatientProfile = async (patientId) => {
    try {
      setLoadingProfile(true);
      setSelectedPatient(patientId);
      setShowPatientDialog(true);
      
      const profile = await getPatientRiskProfile(patientId, facilityId);
      setPatientRiskProfile(profile);
    } catch (err) {
      console.error('Error fetching patient risk profile:', err);
      setPatientRiskProfile(null);
    } finally {
      setLoadingProfile(false);
    }
  };

  // Render overview tab with summary cards
  const renderOverview = () => (
    <Box>
      {loadingTabs[TABS.OVERVIEW] && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4, mb: 2 }}>
          <CircularProgress size={40} />
          <Typography sx={{ ml: 2 }} color="textSecondary">Loading overview data...</Typography>
        </Box>
      )}
      {/* Summary Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3, opacity: loadingTabs[TABS.OVERVIEW] ? 0.5 : 1 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard color="#f44336">
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <HospitalIcon sx={{ mr: 1 }} />
                <Typography variant="subtitle2" sx={{ opacity: 0.9 }}>
                  Readmission Risk
                </Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 700 }}>
                {readmissionSummary?.summary?.highRiskCount || 0}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                High-risk patients
              </Typography>
            </CardContent>
          </StatCard>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatCard color="#ff9800">
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <NoShowIcon sx={{ mr: 1 }} />
                <Typography variant="subtitle2" sx={{ opacity: 0.9 }}>
                  No-Show Risk Today
                </Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 700 }}>
                {noShowSummary[0]?.highRiskCount || 0}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                High-risk appointments
              </Typography>
            </CardContent>
          </StatCard>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatCard color="#9c27b0">
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <PeopleIcon sx={{ mr: 1 }} />
                <Typography variant="subtitle2" sx={{ opacity: 0.9 }}>
                  Population at Risk
                </Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 700 }}>
                {populationSummary?.summary?.riskDistribution?.find(r => r.tier === 'high_risk')?.patientCount || 0}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                High risk tier
              </Typography>
            </CardContent>
          </StatCard>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatCard color="#4caf50">
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <AssessmentIcon sx={{ mr: 1 }} />
                <Typography variant="subtitle2" sx={{ opacity: 0.9 }}>
                  ML Models Active
                </Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 700 }}>
                {modelPerformance?.summary?.totalModels || 4}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                Predictive models
              </Typography>
            </CardContent>
          </StatCard>
        </Grid>
      </Grid>

      {/* Quick View Tables */}
      <Grid container spacing={3}>
        {/* High Risk Readmissions */}
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, backgroundColor: 'white', height: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" component="div" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', color: '#333' }}>
                <HospitalIcon sx={{ mr: 1, color: '#f44336' }} />
                High Readmission Risk
              </Typography>
              <Button 
                size="small" 
                onClick={() => setActiveTab(TABS.READMISSION)}
                sx={{ color: '#667eea' }}
              >
                View All
              </Button>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                    <TableCell sx={{ fontWeight: 600, color: '#555' }}>Patient</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#555' }}>MRN</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#555' }}>Risk</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#555' }}>Diagnosis</TableCell>
                  </TableRow>
                </TableHead>
                  <TableBody>
                    {highRiskReadmissions.slice(0, 5).map((patient, index) => (
                      <TableRow key={`${patient.patientId}-${patient.encounterId || index}`} hover>
                        <TableCell>
                          <Typography 
                            variant="body2" 
                            sx={{ cursor: 'pointer', color: 'primary.main' }}
                            onClick={() => handleViewPatientProfile(patient.patientId)}
                          >
                            {patient.patientName || patient.patientId.substring(0, 8) + '...'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {patient.mrn || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <RiskChip 
                            label={`${patient.riskPercentage}%`}
                            riskcolor={getRiskTierColor(patient.riskTier)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                            {patient.diagnosis}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                    {highRiskReadmissions.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                          <Typography variant="body2" color="textSecondary">
                            No high-risk patients found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
          </Paper>
        </Grid>

        {/* Population Risk Distribution */}
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, backgroundColor: 'white', height: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" component="div" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', color: '#333' }}>
                <PeopleIcon sx={{ mr: 1, color: '#9c27b0' }} />
                Population Risk Distribution
              </Typography>
              <Button 
                size="small" 
                onClick={() => setActiveTab(TABS.POPULATION)}
                sx={{ color: '#667eea' }}
              >
                View All
              </Button>
            </Box>
            {populationSummary?.summary?.riskDistribution?.map((tier, index) => (
              <Box key={`${tier.tier || index}`} sx={{ mb: 2.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>
                    {getRiskTierLabel(tier.tier)}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500, color: '#666' }}>
                    {tier.patientCount?.toLocaleString()} ({tier.percentage}%)
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={tier.percentage}
                  sx={{
                    height: 10,
                    borderRadius: 5,
                    backgroundColor: 'rgba(0,0,0,0.08)',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: getRiskTierColor(tier.tier),
                      borderRadius: 5,
                    }
                  }}
                />
              </Box>
            ))}
            {(!populationSummary?.summary?.riskDistribution || populationSummary.summary.riskDistribution.length === 0) && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <PeopleIcon sx={{ fontSize: 48, color: '#ccc', mb: 2 }} />
                <Typography variant="body2" color="textSecondary">
                  No data available
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );

  // Render readmission risk tab
  const renderReadmissionRisk = () => (
    <Box>
      {loadingTabs[TABS.READMISSION] && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4, mb: 2 }}>
          <CircularProgress size={40} />
          <Typography sx={{ ml: 2 }} color="textSecondary">Loading readmission data...</Typography>
        </Box>
      )}
      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3, opacity: loadingTabs[TABS.READMISSION] ? 0.5 : 1 }}>
        {readmissionSummary?.byRiskTier?.map((tier, index) => (
          <Grid item xs={12} sm={6} md={3} key={`${tier.tier || index}`}>
            <ModernCard>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Box 
                    sx={{ 
                      width: 12, 
                      height: 12, 
                      borderRadius: '50%', 
                      backgroundColor: getRiskTierColor(tier.tier),
                      mr: 1 
                    }} 
                  />
                  <Typography variant="subtitle2" color="textSecondary">
                    {getRiskTierLabel(tier.tier)}
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {tier.patientCount}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Avg probability: {tier.averageProbability}%
                </Typography>
              </CardContent>
            </ModernCard>
          </Grid>
        ))}
      </Grid>

      {/* High Risk Patients Table */}
      <ModernCard>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              High-Risk Readmission Patients
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {highRiskReadmissions.length} patients
            </Typography>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Patient</TableCell>
                  <TableCell>Age</TableCell>
                  <TableCell>Diagnosis</TableCell>
                  <TableCell>LOS (days)</TableCell>
                  <TableCell>Comorbidity Index</TableCell>
                  <TableCell>Risk Score</TableCell>
                  <TableCell>Risk Tier</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {highRiskReadmissions
                  .slice(readmissionPage * readmissionRowsPerPage, readmissionPage * readmissionRowsPerPage + readmissionRowsPerPage)
                  .map((patient, index) => (
                  <TableRow key={`${patient.patientId}-${patient.encounterId || index}`} hover>
                    <TableCell>{patient.patientName || patient.patientId.substring(0, 12) + '...'}</TableCell>
                    <TableCell>{patient.age}</TableCell>
                    <TableCell sx={{ textTransform: 'capitalize' }}>{patient.diagnosis}</TableCell>
                    <TableCell>{patient.losdays}</TableCell>
                    <TableCell>{patient.comorbidityIndex}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {patient.riskPercentage}%
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <RiskChip 
                        label={patient.riskTier}
                        riskcolor={getRiskTierColor(patient.riskTier)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title="View Risk Profile">
                        <IconButton 
                          size="small" 
                          onClick={() => handleViewPatientProfile(patient.patientId)}
                        >
                          <PersonIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {highRiskReadmissions.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="body2" color="textSecondary">
                        No high-risk readmission patients found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={highRiskReadmissions.length}
            page={readmissionPage}
            onPageChange={(e, newPage) => setReadmissionPage(newPage)}
            rowsPerPage={readmissionRowsPerPage}
            onRowsPerPageChange={(e) => {
              setReadmissionRowsPerPage(parseInt(e.target.value, 10));
              setReadmissionPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </CardContent>
      </ModernCard>
    </Box>
  );

  // Render no-show predictions tab
  const renderNoShowPredictions = () => (
    <Box>
      {loadingTabs[TABS.NOSHOW] && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4, mb: 2 }}>
          <CircularProgress size={40} />
          <Typography sx={{ ml: 2 }} color="textSecondary">Loading no-show predictions...</Typography>
        </Box>
      )}
      {/* Daily Summary Cards */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, backgroundColor: 'white', opacity: loadingTabs[TABS.NOSHOW] ? 0.5 : 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: '#333' }}>
          Next 7 Days No-Show Forecast
        </Typography>
        <Grid container spacing={2}>
          {noShowSummary.length > 0 ? (
            noShowSummary.slice(0, 7).map((day, index) => (
              <Grid item xs={6} sm={4} md key={day.date || index}>
                <Card 
                  sx={{ 
                    textAlign: 'center', 
                    borderRadius: 2,
                    border: '1px solid #e0e0e0',
                    boxShadow: 'none',
                    transition: 'all 0.2s',
                    '&:hover': {
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      transform: 'translateY(-2px)'
                    }
                  }}
                >
                  <CardContent sx={{ py: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#667eea', mb: 1 }}>
                      {day.date ? new Date(day.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : 'Unknown'}
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, my: 1, color: '#333' }}>
                      {day.totalAppointments}
                    </Typography>
                    <Typography variant="caption" display="block" color="textSecondary" sx={{ mb: 1 }}>
                      appointments
                    </Typography>
                    <Divider sx={{ my: 1.5 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
                      <Box>
                        <Typography variant="h6" sx={{ color: '#f44336', fontWeight: 700 }}>
                          {day.highRiskCount}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">High Risk</Typography>
                      </Box>
                      <Box>
                        <Typography variant="h6" sx={{ color: '#ff9800', fontWeight: 700 }}>
                          {day.mediumRiskCount}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">Medium</Typography>
                      </Box>
                    </Box>
                    <Box sx={{ mt: 1.5, py: 0.5, px: 1, backgroundColor: '#e8f5e9', borderRadius: 1 }}>
                      <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 500 }}>
                        ~{Math.round(day.expectedNoShows)} expected no-shows
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))
          ) : (
            <Grid item xs={12}>
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <NoShowIcon sx={{ fontSize: 48, color: '#ccc', mb: 2 }} />
                <Typography variant="body1" color="textSecondary">
                  No upcoming appointment data available
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* High Risk Appointments Table */}
      <Paper elevation={0} sx={{ p: 3, borderRadius: 3, backgroundColor: 'white' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#333' }}>
            High No-Show Risk Appointments
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {noShowPredictions.filter(apt => apt.noShowRisk === 'HIGH' || apt.noShowRisk === 'MEDIUM').length} appointments
          </Typography>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                <TableCell sx={{ fontWeight: 600, color: '#555' }}>Patient</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#555' }}>MRN</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#555' }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#555' }}>Time</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#555' }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#555' }}>History Rate</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#555' }}>Risk Score</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#555' }}>Recommended Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {noShowPredictions
                .filter(apt => apt.noShowRisk === 'HIGH' || apt.noShowRisk === 'MEDIUM')
                .slice(noShowPage * noShowRowsPerPage, noShowPage * noShowRowsPerPage + noShowRowsPerPage)
                .map((apt) => (
                  <TableRow 
                    key={apt.appointmentId} 
                    hover
                    sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {apt.patientName || (apt.patientId ? apt.patientId.substring(0, 12) + '...' : '-')}
                      </Typography>
                    </TableCell>
                    <TableCell>{apt.mrn || '-'}</TableCell>
                    <TableCell>
                      {apt.date ? new Date(apt.date).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell>{apt.timeSlot || '-'}</TableCell>
                    <TableCell sx={{ textTransform: 'capitalize' }}>{apt.type || '-'}</TableCell>
                    <TableCell>
                      {formatProbabilityPercent(apt.historicalNoShowRate)}
                    </TableCell>
                    <TableCell>
                      <RiskChip
                        label={`${apt.riskPercentage}%`}
                        riskcolor={getRiskTierColor(apt.noShowRisk)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={
                          apt.recommendedAction === 'CALL_CONFIRM_24HR' ? <PhoneIcon /> :
                          apt.recommendedAction === 'SMS_REMINDER' ? <SmsIcon /> :
                          <CheckIcon />
                        }
                        label={getNoShowActionText(apt.recommendedAction)}
                        size="small"
                        variant="outlined"
                        sx={{ borderColor: '#667eea', color: '#667eea' }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              {noShowPredictions.filter(apt => apt.noShowRisk === 'HIGH' || apt.noShowRisk === 'MEDIUM').length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <NoShowIcon sx={{ fontSize: 40, color: '#ccc', mb: 1 }} />
                    <Typography variant="body1" color="textSecondary">
                      No high-risk appointments found
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                      All upcoming appointments have low no-show risk
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={noShowPredictions.filter(apt => apt.noShowRisk === 'HIGH' || apt.noShowRisk === 'MEDIUM').length}
          page={noShowPage}
          onPageChange={(e, newPage) => setNoShowPage(newPage)}
          rowsPerPage={noShowRowsPerPage}
          onRowsPerPageChange={(e) => {
            setNoShowRowsPerPage(parseInt(e.target.value, 10));
            setNoShowPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </Paper>
    </Box>
  );

  // Render population health tab
  const renderPopulationHealth = () => (
    <Box>
      {loadingTabs[TABS.POPULATION] && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4, mb: 2 }}>
          <CircularProgress size={40} />
          <Typography sx={{ ml: 2 }} color="textSecondary">Loading population health data...</Typography>
        </Box>
      )}
      {/* Summary Stats */}
      <Grid container spacing={3} sx={{ mb: 3, opacity: loadingTabs[TABS.POPULATION] ? 0.5 : 1 }}>
        <Grid item xs={12} md={4}>
          <ModernCard>
            <CardContent>
              <Typography variant="subtitle2" color="textSecondary">
                Total Patients
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 700 }}>
                {populationSummary?.summary?.totalPatients?.toLocaleString() || 0}
              </Typography>
            </CardContent>
          </ModernCard>
        </Grid>
        <Grid item xs={12} md={4}>
          <ModernCard>
            <CardContent>
              <Typography variant="subtitle2" color="textSecondary">
                Total Annual Healthcare Costs
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 700 }}>
                ${(populationSummary?.summary?.totalAnnualCosts / 1000000)?.toFixed(1) || 0}M
              </Typography>
            </CardContent>
          </ModernCard>
        </Grid>
        <Grid item xs={12} md={4}>
          <ModernCard>
            <CardContent>
              <Typography variant="subtitle2" color="textSecondary">
                High/Rising Risk Patients
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 700, color: '#f44336' }}>
                {(populationSummary?.summary?.riskDistribution || [])
                  .filter(r => r.tier === 'high_risk' || r.tier === 'rising_risk')
                  .reduce((sum, r) => sum + r.patientCount, 0)
                  .toLocaleString()}
              </Typography>
            </CardContent>
          </ModernCard>
        </Grid>
      </Grid>

      {/* Risk Tier Details Table */}
      <ModernCard sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Risk Tier Analysis
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Risk Tier</TableCell>
                  <TableCell align="right">Patients</TableCell>
                  <TableCell align="right">% of Population</TableCell>
                  <TableCell align="right">Avg Age</TableCell>
                  <TableCell align="right">Avg Conditions</TableCell>
                  <TableCell align="right">Avg Medications</TableCell>
                  <TableCell align="right">Avg Annual Cost</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {populationSummary?.summary?.riskDistribution?.map((tier, index) => (
                  <TableRow key={`${tier.tier || index}`}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box 
                          sx={{ 
                            width: 12, 
                            height: 12, 
                            borderRadius: '50%', 
                            backgroundColor: getRiskTierColor(tier.tier),
                            mr: 1 
                          }} 
                        />
                        {getRiskTierLabel(tier.tier)}
                      </Box>
                    </TableCell>
                    <TableCell align="right">{tier.patientCount.toLocaleString()}</TableCell>
                    <TableCell align="right">{tier.percentage}%</TableCell>
                    <TableCell align="right">{tier.avgAge}</TableCell>
                    <TableCell align="right">{tier.avgChronicConditions}</TableCell>
                    <TableCell align="right">{tier.avgMedications}</TableCell>
                    <TableCell align="right">${tier.avgAnnualCosts.toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </ModernCard>

      {/* High Risk Patients for Care Management */}
      <ModernCard>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              High-Risk Patients for Care Management
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {highRiskPatients.length} patients
            </Typography>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Patient</TableCell>
                  <TableCell>MRN</TableCell>
                  <TableCell>Age</TableCell>
                  <TableCell>Risk Tier</TableCell>
                  <TableCell>Risk Score</TableCell>
                  <TableCell>Conditions</TableCell>
                  <TableCell>Medications</TableCell>
                  <TableCell>Risk Factors</TableCell>
                  <TableCell>Intervention</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {highRiskPatients
                  .slice(populationPage * populationRowsPerPage, populationPage * populationRowsPerPage + populationRowsPerPage)
                  .map((patient, index) => {
                  const intervention = getInterventionDetails(patient.recommendedIntervention);
                  return (
                    <TableRow key={`${patient.patientId}-${index}`} hover>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ cursor: 'pointer', color: 'primary.main' }}
                          onClick={() => handleViewPatientProfile(patient.patientId)}
                        >
                          {patient.patientName || patient.patientId.substring(0, 12) + '...'}
                        </Typography>
                      </TableCell>
                      <TableCell>{patient.mrn || '-'}</TableCell>
                      <TableCell>{patient.age}</TableCell>
                      <TableCell>
                        <RiskChip 
                          label={patient.riskTier}
                          riskcolor={getRiskTierColor(patient.riskTier)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{patient.riskScore}</TableCell>
                      <TableCell>{patient.chronicDiseaseCount}</TableCell>
                      <TableCell>{patient.medicationCount}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {patient.riskFactors?.slice(0, 2).map((factor, idx) => (
                            <Chip key={idx} label={factor} size="small" variant="outlined" />
                          ))}
                          {patient.riskFactors?.length > 2 && (
                            <Chip label={`+${patient.riskFactors.length - 2}`} size="small" variant="outlined" />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={intervention.description}>
                          <Chip 
                            icon={<span>{intervention.icon}</span>}
                            label={patient.recommendedIntervention}
                            size="small"
                            color={intervention.priority === 1 ? 'error' : intervention.priority === 2 ? 'warning' : 'default'}
                          />
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {highRiskPatients.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Typography variant="body2" color="textSecondary">
                        No high-risk patients found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={highRiskPatients.length}
            page={populationPage}
            onPageChange={(e, newPage) => setPopulationPage(newPage)}
            rowsPerPage={populationRowsPerPage}
            onRowsPerPageChange={(e) => {
              setPopulationRowsPerPage(parseInt(e.target.value, 10));
              setPopulationPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </CardContent>
      </ModernCard>
    </Box>
  );

  // Patient Risk Profile Dialog
  const PatientProfileDialog = () => (
    <Dialog 
      open={showPatientDialog} 
      onClose={() => setShowPatientDialog(false)}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        Patient Risk Profile
        {selectedPatient && (
          <Typography variant="subtitle2" color="textSecondary">
            ID: {selectedPatient}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {loadingProfile ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : patientRiskProfile ? (
          <Grid container spacing={3}>
            {/* Overall Risk */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Overall Risk Assessment
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <RiskChip 
                  label={patientRiskProfile.overallRisk?.tier}
                  riskcolor={getRiskTierColor(patientRiskProfile.overallRisk?.tier)}
                  sx={{ mr: 2 }}
                />
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  Score: {patientRiskProfile.overallRisk?.score}
                </Typography>
              </Box>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Risk Factors:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {patientRiskProfile.overallRisk?.factors?.map((factor, idx) => (
                  <Chip key={idx} label={factor} size="small" icon={<WarningIcon />} />
                ))}
              </Box>
            </Grid>

            {/* Readmission Risk */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                30-Day Readmission Risk
              </Typography>
              {patientRiskProfile.readmissionRisk?.probability !== null ? (
                <>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: getRiskTierColor(patientRiskProfile.readmissionRisk?.tier) }}>
                    {patientRiskProfile.readmissionRisk?.percentage}%
                  </Typography>
                  <RiskChip 
                    label={patientRiskProfile.readmissionRisk?.tier}
                    riskcolor={getRiskTierColor(patientRiskProfile.readmissionRisk?.tier)}
                    size="small"
                  />
                </>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  No recent admission data
                </Typography>
              )}
            </Grid>

            {/* Clinical Profile */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Clinical Profile
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText 
                    primary="Comorbidity Index" 
                    secondary={patientRiskProfile.clinicalProfile?.comorbidityIndex} 
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Chronic Conditions" 
                    secondary={patientRiskProfile.clinicalProfile?.chronicConditions} 
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Active Medications" 
                    secondary={patientRiskProfile.clinicalProfile?.activeMedications} 
                  />
                </ListItem>
              </List>
            </Grid>

            {/* Utilization */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Healthcare Utilization
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText 
                    primary="Admissions (last year)" 
                    secondary={patientRiskProfile.utilization?.admissionsLastYear} 
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="ED Visits (last 6 mo)" 
                    secondary={patientRiskProfile.utilization?.edVisitsLast6Mo} 
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Days Since Last Visit" 
                    secondary={patientRiskProfile.utilization?.daysSinceLastVisit} 
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Annual Costs" 
                    secondary={`$${patientRiskProfile.utilization?.annualCosts?.toLocaleString()}`} 
                  />
                </ListItem>
              </List>
            </Grid>

            {/* Recommendations */}
            <Grid item xs={12}>
              <Alert 
                severity={patientRiskProfile.recommendations?.intervention === 'Care Management Enrollment' ? 'error' : 'info'}
                icon={<InfoIcon />}
              >
                <Typography variant="subtitle2">
                  Recommended Intervention: {patientRiskProfile.recommendations?.intervention}
                </Typography>
                {patientRiskProfile.recommendations?.priorityActions?.length > 0 && (
                  <Typography variant="body2">
                    Priority Actions: {patientRiskProfile.recommendations.priorityActions.join(', ')}
                  </Typography>
                )}
              </Alert>
            </Grid>
          </Grid>
        ) : (
          <Alert severity="warning">
            Unable to load patient risk profile
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setShowPatientDialog(false)}>Close</Button>
      </DialogActions>
    </Dialog>
  );

  // Main render
  if (!facilityId) {
    return (
      <Box sx={{ p: 4, background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)', minHeight: '100vh' }}>
        <Alert severity="warning">
          No facility access available. Please contact your administrator.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4, background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)', minHeight: '100vh' }}>
      {/* Page Header */}
      <HeaderCard>
        <CardContent sx={{ py: 2, px: 3, '&:last-child': { pb: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <TrendingUpIcon sx={{ fontSize: 32, color: 'white' }} />
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'white', mb: 0.5 }}>
                  Predictive Analytics
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                  AI-powered risk predictions and population health insights
                </Typography>
              </Box>
            </Box>
            <Tooltip title="Refresh Data">
              <span>
                <IconButton onClick={fetchData} disabled={loading} sx={{ color: 'white' }}>
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </CardContent>
      </HeaderCard>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Sub-tabs */}
      <Paper sx={{ mb: 3, borderRadius: 2 }}>
        <Tabs 
          value={activeTab} 
          onChange={(e, v) => setActiveTab(v)}
          variant="fullWidth"
          sx={{
            '& .MuiTab-root': {
              minHeight: 56,
              textTransform: 'none',
              fontWeight: 500,
            }
          }}
        >
          <Tab 
            icon={<AssessmentIcon />} 
            label="Overview" 
            iconPosition="start"
          />
          <Tab 
            icon={<HospitalIcon />} 
            label="Readmission Risk" 
            iconPosition="start"
          />
          <Tab 
            icon={<NoShowIcon />} 
            label="No-Show Predictions" 
            iconPosition="start"
          />
          <Tab 
            icon={<PeopleIcon />} 
            label="Population Health" 
            iconPosition="start"
          />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      <Box sx={{ minHeight: 400 }}>
        {activeTab === TABS.OVERVIEW && renderOverview()}
        {activeTab === TABS.READMISSION && renderReadmissionRisk()}
        {activeTab === TABS.NO_SHOW && renderNoShowPredictions()}
        {activeTab === TABS.POPULATION && renderPopulationHealth()}
      </Box>

      {/* Dialogs */}
      <PatientProfileDialog />
    </Box>
  );
}
